import React, { useEffect, useRef, useState } from 'react'
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { IconButtonWithTooltip } from 'components'
import {
  type ExecOrg,
  type ExecOrgSearchItem,
  searchExecutiveOrganizations
} from 'services/executives/executiveOrgLookupService'

interface ReparentPositionDialogProps {
  open: boolean
  onClose: () => void
  roleId: number
  stateId: number | null
  currentOrgId: number
  onSelect: (org: ExecOrg) => void
}

const optionLabel = (o: ExecOrgSearchItem): string =>
  o.code != null && o.code !== '' ? `${o.name} (${o.code})` : o.name

export const ReparentPositionDialog: React.FC<ReparentPositionDialogProps> = ({
  open,
  onClose,
  roleId,
  stateId,
  currentOrgId,
  onSelect
}) => {
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<ExecOrgSearchItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<ExecOrgSearchItem | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | undefined>()

  useEffect(() => {
    if (open) {
      setInputValue('')
      setOptions([])
      setTotal(0)
      setSelected(null)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      setLoading(true)
      searchExecutiveOrganizations({
        name_or_parent: inputValue.trim() !== '' ? inputValue.trim() : undefined,
        role_id: roleId,
        state_id: stateId ?? undefined,
        page_size: 25
      })
        .then((results) => {
          if (cancelled) return
          const visible = results.data.filter(o => o.id !== currentOrgId)
          setOptions(visible)
          setTotal(results.total_items - (results.data.length - visible.length))
        })
        .catch(() => { if (!cancelled) { setOptions([]); setTotal(0) } })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, 400)
    return () => { cancelled = true; clearTimeout(timeoutRef.current) }
  }, [inputValue, open, roleId, stateId, currentOrgId])

  const handleConfirm = (): void => {
    if (selected == null) return
    onSelect({ id: selected.id, name: selected.name, code: selected.code, topId: selected.topId })
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Move to Another Organization
        <IconButtonWithTooltip label='Close' color='secondary' icon={<CloseIcon />} onClick={onClose} />
      </DialogTitle>
      <DialogContent>
        <Autocomplete<ExecOrgSearchItem>
          sx={{ mt: 2 }}
          options={options}
          loading={loading}
          value={selected}
          inputValue={inputValue}
          filterOptions={(x) => x}
          onChange={(_, next) => { setSelected(next) }}
          onInputChange={(_, next) => { setInputValue(next) }}
          getOptionLabel={optionLabel}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderOption={(props, o) => (
            <li {...props} key={o.id}>
              {optionLabel(o)}{o.parentOrg != null ? ` — ${o.parentOrg.name}` : ''}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label='Search organizations'
              autoFocus
              helperText={total > options.length ? `Showing ${options.length} of ${total}. Type to narrow the list.` : undefined}
            />
          )}
          noOptionsText={loading ? 'Searching…' : 'No matching organizations'}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='secondary'>Cancel</Button>
        <Button onClick={handleConfirm} disabled={selected == null} variant='contained'>Move</Button>
      </DialogActions>
    </Dialog>
  )
}
